<template>
    <div class="resource-tags">
        <div class="resource-tags-header">
            <span class="resource-tags-label">Tags</span>
            <Button icon="pi pi-plus" text rounded size="small" severity="secondary" v-tooltip.top="'Add tag'" @click="openAddDialog" />
        </div>
        <div class="resource-tags-list" v-if="resourceTags.length > 0">
            <span
                v-for="tag in resourceTags"
                :key="tag.tagId"
                class="resource-tag-chip"
                :style="{ backgroundColor: tag.color, color: getContrastColor(tag.color) }"
            >
                {{ tag.label }}
                <i class="pi pi-times resource-tag-remove" @click="removeTag(tag)"></i>
            </span>
        </div>
        <div v-else class="resource-tags-empty">No tags</div>

        <!-- Add Tag Dialog -->
        <Dialog v-model:visible="showAddDialog" header="Add Tag" modal :style="{ width: '400px' }">
            <div class="add-tag-form">
                <!-- Mode toggle -->
                <div class="mode-toggle">
                    <Button
                        :label="'Existing'"
                        :severity="mode === 'existing' ? 'primary' : 'secondary'"
                        :text="mode !== 'existing'"
                        size="small"
                        @click="mode = 'existing'"
                    />
                    <Button
                        :label="'Create new'"
                        :severity="mode === 'create' ? 'primary' : 'secondary'"
                        :text="mode !== 'create'"
                        size="small"
                        @click="mode = 'create'"
                    />
                </div>

                <!-- Existing tag selection -->
                <div v-if="mode === 'existing'" class="form-section">
                    <Select
                        v-model="selectedTagId"
                        :options="availableTags"
                        optionLabel="label"
                        optionValue="tagId"
                        placeholder="Select a tag"
                        class="w-full"
                        filter
                    >
                        <template #option="{ option }">
                            <div class="tag-option">
                                <span class="tag-option-dot" :style="{ backgroundColor: option.color }"></span>
                                <span>{{ option.label }}</span>
                            </div>
                        </template>
                    </Select>
                </div>

                <!-- Create new tag form -->
                <div v-else class="form-section">
                    <div class="field">
                        <label for="new-tag-label">Label</label>
                        <InputText id="new-tag-label" v-model="newTagForm.label" placeholder="Tag name" class="w-full" />
                    </div>
                    <div class="field">
                        <label for="new-tag-description">Description</label>
                        <InputText id="new-tag-description" v-model="newTagForm.description" placeholder="Optional description" class="w-full" />
                    </div>
                    <div class="field">
                        <label for="new-tag-color">Color</label>
                        <ColorPicker id="new-tag-color" v-model="newTagForm.color" />
                    </div>
                </div>
            </div>
            <template #footer>
                <Button label="Cancel" text @click="showAddDialog = false" />
                <Button
                    v-if="mode === 'existing'"
                    label="Add"
                    @click="addTag"
                    :loading="adding"
                    :disabled="!selectedTagId"
                />
                <Button
                    v-else
                    label="Create & Add"
                    @click="createAndAddTag"
                    :loading="adding"
                    :disabled="!newTagForm.label.trim()"
                />
            </template>
        </Dialog>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useApi } from '@/api'
import { handleApiError } from '@/lib/utils'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import ColorPicker from 'primevue/colorpicker'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'

interface TagInfo {
    tagId: number
    label: string
    description: string | null
    color: string
}

const props = defineProps<{
    resourceType: 'vessel' | 'account' | 'monitor' | 'sensor'
    resourceId: string
}>()

const toast = useToast()
const resourceTags = ref<TagInfo[]>([])
const allTags = ref<TagInfo[]>([])
const showAddDialog = ref(false)
const selectedTagId = ref<number | null>(null)
const adding = ref(false)
const mode = ref<'existing' | 'create'>('existing')

const newTagForm = ref({
    label: '',
    description: '',
    color: '3B82F6'
})

const availableTags = computed(() => {
    const linkedIds = new Set(resourceTags.value.map(t => t.tagId))
    return allTags.value.filter(t => !linkedIds.has(t.tagId))
})

const getContrastColor = (hex: string): string => {
    const color = hex.replace('#', '')
    const r = Number.parseInt(color.substring(0, 2), 16)
    const g = Number.parseInt(color.substring(2, 4), 16)
    const b = Number.parseInt(color.substring(4, 6), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#ffffff'
}

const openAddDialog = () => {
    mode.value = 'existing'
    selectedTagId.value = null
    newTagForm.value = { label: '', description: '', color: '3B82F6' }
    showAddDialog.value = true
}

const loadResourceTags = async () => {
    handleApiError(async () => {
        const result = await useApi().getTagsForResourceV1({
            resourceType: props.resourceType as any,
            resourceId: props.resourceId
        })
        resourceTags.value = result.tags as TagInfo[]
    })
}

const loadAllTags = async () => {
    handleApiError(async () => {
        const result = await useApi().getTagsV1({})
        allTags.value = (result.tags as any[]).map(t => ({
            tagId: t.tagId,
            label: t.label,
            description: t.description,
            color: t.color
        }))
    })
}

const addTag = async () => {
    if (!selectedTagId.value) return
    adding.value = true
    handleApiError(
        async () => {
            await useApi().postTagResourceV1({
                tagId: selectedTagId.value!,
                postTagResourceV1Body: {
                    resourceType: props.resourceType as any,
                    resourceId: props.resourceId
                }
            })
            toast.add({ severity: 'success', summary: 'Tag added', life: 3000 })
            showAddDialog.value = false
            selectedTagId.value = null
            await loadResourceTags()
        },
        async () => {
            adding.value = false
        }
    )
}

const createAndAddTag = async () => {
    if (!newTagForm.value.label.trim()) return
    adding.value = true
    handleApiError(
        async () => {
            const api = useApi()
            const color = newTagForm.value.color.startsWith('#') ? newTagForm.value.color : `#${newTagForm.value.color}`

            // Create the tag
            const created = await api.postTagV1({
                postTagV1Body: {
                    label: newTagForm.value.label.trim(),
                    description: newTagForm.value.description.trim() || undefined,
                    color
                }
            })

            // Link it to this resource
            await api.postTagResourceV1({
                tagId: created.tagId,
                postTagResourceV1Body: {
                    resourceType: props.resourceType as any,
                    resourceId: props.resourceId
                }
            })

            toast.add({ severity: 'success', summary: 'Tag created and added', life: 3000 })
            showAddDialog.value = false
            newTagForm.value = { label: '', description: '', color: '3B82F6' }
            await loadResourceTags()
            await loadAllTags()
        },
        async () => {
            adding.value = false
        }
    )
}

const removeTag = async (tag: TagInfo) => {
    handleApiError(async () => {
        await useApi().deleteTagResourceV1({
            tagId: tag.tagId,
            resourceType: props.resourceType as any,
            resourceId: props.resourceId
        })
        toast.add({ severity: 'success', summary: 'Tag removed', life: 3000 })
        await loadResourceTags()
    })
}

watch(() => [props.resourceType, props.resourceId], () => {
    if (props.resourceId) {
        loadResourceTags()
    }
})

onMounted(() => {
    if (props.resourceId) {
        loadResourceTags()
    }
    loadAllTags()
})
</script>

<style lang="scss" scoped>
.resource-tags {
    padding: 8px 0;
}

.resource-tags-header {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
}

.resource-tags-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.resource-tags-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.resource-tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
}

.resource-tag-remove {
    font-size: 0.6rem;
    cursor: pointer;
    opacity: 0.7;
    &:hover {
        opacity: 1;
    }
}

.resource-tags-empty {
    font-size: 0.8rem;
    color: #9ca3af;
}

.add-tag-form {
    padding: 0.5rem 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.mode-toggle {
    display: flex;
    gap: 8px;
}

.form-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    label {
        font-size: 0.8rem;
        font-weight: 600;
        color: #374151;
    }
}

.tag-option {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tag-option-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.w-full {
    width: 100%;
}
</style>
